import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Carousel from "react-multi-carousel";
import { useDispatch } from "react-redux";
import OTPInput from "react-otp-input";
import { Input, Label, Button, Form, FormFeedback } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CircularProgress, Link, styled } from "@mui/material";

import InputField from "Components/Common/loginInputField";
import SignupModal from "pages/Home/SignupModal";
import HeaderSection from "pages/Home/Header";
import ForgotPasswordModal from "pages/Home/ForgotPasswordModal";
import { loginSuccess } from "slices/auth/login/reducer";
import { otpVerificationType } from "common/constant";
import Backdrop from "Components/Common/Backdrop";
import Loader from "Components/Common/Loader";

import { fireSafetyHomeRoute } from "Routes/constants";
import useCountdown from "hooks/useCountDown";

import { ImagePreloader } from "utils";
import decodeToken from "utils/decodeToken";

import { useLazyGetUserQuery } from "apis/User";
import { useRequestOTPMutation, useValidateOTPPutMutation } from "apis/OTP";

import colors from "assets/scss/colors.module.scss";
import giftcityLogo from "assets/images/banner-logo.webp";
// import { ReactComponent as LeftArrow } from "assets/images/svg/whiteleftarrow.svg";
import fireSafety from "assets/images/fire-safety.jpg";
import heroSectionImg1 from "assets/images/hero-sectionbg.png";
import heroSectionImg2 from "assets/images/hero-sectionbg2.jpg";
import heroSectionImg3 from "assets/images/hero-sectionbg3.jpg";

import "react-multi-carousel/lib/styles.css";
import { usePostLoginUserMutation } from "apis/FireSafety";
import { useGetMenuItemsQuery } from "apis/HomePage";

const color = colors.secondarygrey;

const carouselImages = [
  fireSafety,
  heroSectionImg1,
  heroSectionImg2,
  heroSectionImg3,
];
const carouselOptions = {
  responsive: {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 1,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 1,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
    },
  },
  swipeable: false,
  draggable: false,
  showDots: false,
  infinite: true,
  autoPlay: true,
  autoPlaySpeed: 5000,
  keyBoardControl: true,
  customTransition: "all .4s",
  transitionDuration: 500,
  containerClass: "carousel-container",
  removeArrowOnDeviceType: ["desktop", "tablet", "mobile"],
  deviceType: "desktop",
  dotListClass: "custom-dot-list-style",
  itemClass: "carousel-item-padding-40-px",
};

const requestOTPType = otpVerificationType["Login"];

export const ResendOTPButton = styled(Link)(
  () => `
    cursor :  pointer;
    color:${colors.primary};
    font-weight:bold;
    font-size:14px;
    :disabled {
    color: ${color};
    cursor:default
    }
`
);

const loginInitialValuesPassword = {
  userName: "",
  password: "",
};

const loginInitialValuesOTP = {
  userName: "",
  otp: "",
};

const loginValidationSchemaPassword = Yup.object({
  userName: Yup.string()
    .matches(
      /^(\d{10}|[^\s@]+@[^\s@]+\.[^\s@]+|[a-zA-Z0-9_]{1,100})$/,
      "Please enter valid email or mobile number"
    )
    .required("Please enter email or mobile number"),
  password: Yup.string().required("Please enter your password"),
});

const loginValidationSchemaOTP = Yup.object({
  userName: Yup.string()
    .email("Enter valid email")
    .required("Please enter email"),
  otp: Yup.string()
    .required("Please enter otp")
    .matches(/^[0-9]+$/, "Must be only digits")
    .min(6, "OTP Must be exactly 6 digits")
    .max(6, "OTP Must be exactly 6 digits"),
});

export const FireSafetyHome = () => {
  const [typeOfLogin, setTypeOfLogin] = useState("Password");
  const [signupModal, setsignupModal] = useState(false);
  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);
  const [colorChange, setColorChange] = useState(false);
  const [isRequestOTP, setIsRequestOTP] = useState(false);
  const [isEnableResendOTP, setIsEnableResendOTP] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleCountdownFinished = () => {
    setIsEnableResendOTP(true);
  };

  const { minutes, seconds, startTimer, resetTimer } = useCountdown(
    0,
    2,
    0,
    handleCountdownFinished
  );

  const [login, { isLoading }] = usePostLoginUserMutation();
  const [getUserData, { isLoading: isGettingUserData }] = useLazyGetUserQuery();

  const { data: { data: menuItems = [] } = {}, isLoading: isMenuDataLoading } =
    useGetMenuItemsQuery({}, true);

  const [requestOTP, { isLoading: isRequestOTPLoading }] =
    useRequestOTPMutation();

  const [verifyOTP, { isLoading: isVerifyOtpLoading }] =
    useValidateOTPPutMutation();

  const signupToggle = () => setsignupModal(!signupModal);

  const forgotPasswordToggle = () =>
    setForgotPasswordModal(!forgotPasswordModal);

  const preloadImages = ImagePreloader(carouselImages);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues:
      typeOfLogin === "Password"
        ? loginInitialValuesPassword
        : loginInitialValuesOTP,
    validationSchema:
      typeOfLogin === "Password"
        ? loginValidationSchemaPassword
        : loginValidationSchemaOTP,
    onSubmit: async (values) => {
      try {
        let data;
        if (typeOfLogin === "Password") {
          const { data: passwordData } = await login(values);
          data = passwordData;
        } else {
          const { data: OTPData } = await verifyOTP(
            {
              emailOtp: +values.otp,
              email: values.userName,
              type: requestOTPType,
            },
            {},
            true
          );
          data = OTPData;
        }

        localStorage.setItem("giftCityToken", data.accessToken);
        const { role, userId } = decodeToken(data.accessToken);
        const { data: userData } = await getUserData(userId, {}, true);

        const roles = Array.isArray(role) ? role : [role];

        dispatch(loginSuccess({ roles, user: userData }));

        navigate(fireSafetyHomeRoute, { replace: true });
      } catch (error) {
        console.error(error);
      }
    },
  });

  const {
    errors,
    touched,
    values: { userName, otp },
    setFieldTouched,
  } = formik;

  const handleChangeOtp = (OTP) => {
    formik.setFieldValue("otp", OTP);
  };

  const changeNavbarColor = () => {
    if (window.scrollY >= 4) {
      setColorChange(true);
    } else {
      setColorChange(false);
    }
  };
  window.addEventListener("scroll", changeNavbarColor);

  const handleResendOTP = async () => {
    setIsEnableResendOTP(false);
    resetTimer();
    startTimer();
    try {
      await requestOTP(
        {
          [userName.match(/^[0-9]{10}$/) ? "mobileNumber" : "email"]: userName,
          type: requestOTPType,
        },
        "OTP generated, please check your phone or email.",
        true
      );
    } catch (err) {}
  };

  const handleRequestOTP = async () => {
    try {
      await requestOTP(
        {
          email: userName,
          type: requestOTPType,
        },
        "OTP generated, please check your phone or email.",
        true
      );

      startTimer();
    } catch (error) {
      setIsRequestOTP(false);
    }
  };

  useEffect(() => {
    if (isRequestOTP) {
      if (userName && !errors.userName) {
        handleRequestOTP();
      } else {
        setIsRequestOTP(false);
      }
    }
  }, [isRequestOTP]);

  if (preloadImages.length !== carouselImages.length || isMenuDataLoading)
    return <Loader />;

  return (
    <>
      {signupModal && (
        <SignupModal open={signupModal} toggle={signupToggle} isFireSafety />
      )}
      {!!forgotPasswordModal && (
        <ForgotPasswordModal
          open={forgotPasswordModal}
          toggle={forgotPasswordToggle}
          isFireSafety
        />
      )}
      {/* nav-bar-section  */}

      {preloadImages.length === carouselImages.length && (
        <>
          <div
            className={
              colorChange
                ? "header-nav-wrapper nav-background"
                : "header-nav-wrapper"
            }
          >
            <div className="container-fluid">
              <HeaderSection menuItems={menuItems} isFireSafety />
            </div>
          </div>
          <Carousel
            {...carouselOptions}
            className="carousel-background-wrapper"
          >
            {preloadImages.map((img, index) => (
              <div
                key={index}
                className="carousel-item carousel-background"
                style={{
                  backgroundImage: `url(${img})`,
                }}
              />
            ))}
          </Carousel>
          <section className="login-section">
            <div className="container-fluid">
              <div className="login-wrapper d-flex justify-content-between align-items-center">
                <div className="text-wrapper">
                  <p className="landmark-text regular-font">
                    A CUTTING-EDGE LANDMARK
                  </p>
                  <div className="gift-citylogo-wrapper">
                    <img src={giftcityLogo} alt="gift city" />
                  </div>
                  <div className="welcome-text medium-font">
                    <p>
                      Welcome to the new epicentre of Global Finance and IT.
                    </p>
                  </div>
                </div>

                <div className="login-form-wrapper">
                  {isRequestOTPLoading && (
                    <Backdrop open={isRequestOTPLoading} />
                  )}
                  <h4 className="text-center pb-2 text-white semibold-font login-text">
                    Login
                  </h4>
                  <Form autoComplete="off" onSubmit={formik.handleSubmit}>
                    <InputField
                      id="userName"
                      name="userName"
                      placeholder={
                        typeOfLogin === "Send OTP"
                          ? "Enter Your Email"
                          : "Enter Your Email / Enter Mobile"
                      }
                      formik={formik}
                    />

                    <div className="radio-group">
                      <Label
                        check
                        className="text-white regular-font radiobtn-text"
                      >
                        <Input
                          type="radio"
                          name="typeOfLogin"
                          checked={typeOfLogin === "Password"}
                          value="Password"
                          onChange={() => setTypeOfLogin("Password")}
                          tabIndex={-1}
                        />
                        Password
                      </Label>
                      <Label
                        check
                        className="ps-2 text-white regular-font radiobtn-text"
                      >
                        <Input
                          type="radio"
                          name="typeOfLogin"
                          checked={typeOfLogin === "Send OTP"}
                          value="Send OTP"
                          onChange={() => {
                            setTypeOfLogin("Send OTP");
                            setIsRequestOTP(false);
                          }}
                          tabIndex={-1}
                        />
                        Send OTP
                      </Label>
                    </div>
                    {typeOfLogin === "Password" ? (
                      <InputField
                        id="password"
                        name="password"
                        placeholder="Enter Your Password"
                        type="password"
                        formik={formik}
                      />
                    ) : (
                      <>
                        <div className="d-flex justify-content-between input-field-wrapper w-100">
                          <OTPInput
                            value={otp}
                            inputStyle={`inputField inputFieldOTP w-25 ${
                              !isRequestOTP ? "disableOTP" : ""
                            }`}
                            containerStyle={"justify-content-between"}
                            onChange={handleChangeOtp}
                            numInputs={6}
                            inputType="number"
                            renderSeparator={<span>{"\u2003"}</span>}
                            shouldAutoFocus
                            renderInput={(props) => (
                              <input
                                {...props}
                                type="text"
                                disabled={!isRequestOTP}
                              />
                            )}
                          />
                        </div>
                        {isRequestOTP && touched.otp && errors.otp ? (
                          <FormFeedback className="error-message d-block">
                            {errors.otp}
                          </FormFeedback>
                        ) : null}
                      </>
                    )}

                    {isRequestOTP && typeOfLogin === "Send OTP" ? (
                      <Button
                        className="login-btn d-flex gap-2 justify-content-center semibold-font"
                        type="submit"
                        disabled={isVerifyOtpLoading}
                      >
                        Verify OTP
                        {isVerifyOtpLoading && (
                          <CircularProgress sx={{ color: "#FFF" }} size={30} />
                        )}
                      </Button>
                    ) : typeOfLogin === "Password" ? (
                      <Button
                        className="login-btn d-flex gap-2 justify-content-center semibold-font"
                        type="submit"
                        disabled={isLoading || isGettingUserData}
                      >
                        Login
                        {(isGettingUserData || isLoading) && (
                          <CircularProgress sx={{ color: "#FFF" }} size={30} />
                        )}
                      </Button>
                    ) : (
                      <></>
                    )}
                  </Form>
                  {typeOfLogin === "Send OTP" && !isRequestOTP && (
                    <Button
                      className="login-btn d-flex gap-2 justify-content-center semibold-font"
                      onClick={() => {
                        setFieldTouched("userName", true);
                        setIsRequestOTP(true);
                      }}
                      disabled={!userName}
                    >
                      Send OTP
                    </Button>
                  )}
                  <div className="medium-font signup-text forgot-password-text pb-2 cursor-pointer">
                    {typeOfLogin === "Password" ? (
                      <span onClick={forgotPasswordToggle}>
                        Forgot Your Password ?
                      </span>
                    ) : isRequestOTP ? (
                      <div className="d-flex justify-content-center gap-2">
                        <ResendOTPButton
                          component="button"
                          underline="none"
                          onClick={handleResendOTP}
                          disabled={!isEnableResendOTP}
                        >
                          Resend OTP
                        </ResendOTPButton>
                        {!isEnableResendOTP && (
                          <p className="m-0">
                            in {minutes}:{seconds}
                          </p>
                        )}
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                  <p className="regular-font text-center text-white">
                    New User ?
                    <span
                      className="ms-2 medium-font signup-text cursor-pointer"
                      onClick={signupToggle}
                    >
                      Sign Up
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
};
